export interface PromptExample {
    name: string;
    prompt: string;
}

export interface PromptCategory {
    name: string;
    description: string;
    subcategories?: PromptCategory[];
    prompts?: PromptExample[];
}


export class PromptLibrary {
    static readonly categories: PromptCategory[] = [
        {
            name: 'Mechanical Engineering',
            description: 'Components and assemblies used in mechanical design.',
            subcategories: [
                {
                    name: 'Gears',
                    description: 'Create various types of gears.',
                    prompts: [
                        { name: 'Simple Spur Gear', prompt: 'Create a spur gear with 30 teeth, a module of 2, and a width of 15mm.' },
                        { name: 'Gear Pair', prompt: 'Create a pair of spur gears with a 2:1 ratio. The first gear has 20 teeth and a module of 3.' },
                        { name: 'Helical Gear', prompt: 'Create a helical gear with 25 teeth, a helix angle of 20 degrees, and a module of 2.5.' },
                        { name: 'Bevel Gear', prompt: 'Create a straight bevel gear with 18 teeth and a cone angle of 45 degrees.' },
                        { name: 'Worm Gear Set', prompt: 'Create a worm gear set with a worm having 2 starts and a gear with 40 teeth.' }
                    ]
                },
                {
                    name: 'Bearings',
                    description: 'Create various types of bearings for rotating assemblies.',
                    prompts: [
                        { name: 'Ball Bearing', prompt: 'Create a deep groove ball bearing with inner diameter 25mm, outer diameter 52mm, and width 15mm.' },
                        { name: 'Roller Bearing', prompt: 'Create a cylindrical roller bearing with inner diameter 40mm and outer diameter 80mm.' },
                        { name: 'Thrust Bearing', prompt: 'Create a thrust ball bearing with bore diameter 20mm and outer diameter 35mm.' },
                        { name: 'Tapered Roller Bearing', prompt: 'Create a tapered roller bearing with inner diameter 30mm and outer diameter 62mm.' }
                    ]
                },
                {
                    name: 'Springs',
                    description: 'Create different types of springs.',
                    prompts: [
                        { name: 'Compression Spring', prompt: 'Create a compression spring with 10 coils, wire diameter 2mm, and outer diameter 20mm.' },
                        { name: 'Extension Spring', prompt: 'Create an extension spring with hooks on both ends, 8 coils, and a free length of 50mm.' },
                        { name: 'Torsion Spring', prompt: 'Create a torsion spring with straight legs and 6 coils, designed for 90-degree rotation.' },
                        { name: 'Wave Spring', prompt: 'Create a wave spring washer with 3 waves and an outer diameter of 25mm.' }
                    ]
                },
                {
                    name: 'Shafts & Couplings',
                    description: 'Create shafts and coupling mechanisms.',
                    prompts: [
                        { name: 'Stepped Shaft', prompt: 'Create a stepped shaft with three different diameters: 20mm, 25mm, and 30mm, total length 200mm.' },
                        { name: 'Flexible Coupling', prompt: 'Create a flexible jaw coupling for connecting two 20mm diameter shafts.' },
                        { name: 'Universal Joint', prompt: 'Create a universal joint (U-joint) for transmitting torque between two non-aligned shafts.' },
                        { name: 'Keyed Shaft', prompt: 'Create a shaft with a keyway, diameter 25mm and length 150mm, with a 6mm square key.' }
                    ]
                },
                {
                    name: 'Pulleys & Belts',
                    description: 'Create pulley systems and belt drives.',
                    prompts: [
                        { name: 'V-Belt Pulley', prompt: 'Create a V-belt pulley with 4 grooves, outer diameter 150mm, and bore diameter 25mm.' },
                        { name: 'Timing Belt Pulley', prompt: 'Create a timing belt pulley with 20 teeth for a 5mm pitch belt.' },
                        { name: 'Flat Belt Pulley', prompt: 'Create a flat belt pulley with diameter 100mm and a crowned surface.' },
                        { name: 'Belt Drive System', prompt: 'Create a two-pulley belt drive system with a 2:1 speed ratio.' }
                    ]
                },
                {
                    name: 'Valves',
                    description: 'Create various types of valves for fluid control.',
                    prompts: [
                        { name: 'Ball Valve', prompt: 'Create a ball valve with 1-inch NPT threaded connections and a lever handle.' },
                        { name: 'Gate Valve', prompt: 'Create a gate valve with flanged connections for 2-inch pipe.' },
                        { name: 'Check Valve', prompt: 'Create a swing check valve with 1.5-inch threaded connections.' },
                        { name: 'Relief Valve', prompt: 'Create a pressure relief valve with adjustable spring tension and 0.75-inch connections.' }
                    ]
                },
                {
                    name: 'Pistons & Cylinders',
                    description: 'Create pneumatic and hydraulic cylinders.',
                    prompts: [
                        { name: 'Pneumatic Cylinder', prompt: 'Create a double-acting pneumatic cylinder with 50mm bore, 25mm rod, and 100mm stroke.' },
                        { name: 'Hydraulic Cylinder', prompt: 'Create a hydraulic cylinder with 80mm bore, 40mm rod diameter, and 200mm stroke length.' },
                        { name: 'Piston Assembly', prompt: 'Create a piston assembly with rings, pin, and connecting rod for a 4-stroke engine.' }
                    ]
                },
                {
                    name: 'Seals & Gaskets',
                    description: 'Create sealing components.',
                    prompts: [
                        { name: 'O-Ring', prompt: 'Create an O-ring with 20mm inner diameter and 2mm cross-section.' },
                        { name: 'Oil Seal', prompt: 'Create a radial oil seal for a 25mm shaft with 40mm outer diameter.' },
                        { name: 'Gasket', prompt: 'Create a spiral wound gasket for a 4-inch flange connection.' },
                        { name: 'Mechanical Seal', prompt: 'Create a mechanical seal assembly for a centrifugal pump with 30mm shaft.' }
                    ]
                },
                {
                    name: 'Beams',
                    description: 'Create structural beams.',
                    prompts: [
                        { name: 'I-Beam', prompt: 'Create a 1-meter long IPE 200 I-beam.' },
                        { name: 'Hollow Rectangular Beam', prompt: 'Create a hollow rectangular beam with outer dimensions 100x50mm, a wall thickness of 5mm, and a length of 500mm.' },
                        { name: 'T-Beam', prompt: 'Create a T-beam with flange width 100mm, web height 120mm, and thickness 8mm.' },
                        { name: 'Channel Beam', prompt: 'Create a C-channel beam with 80mm height, 40mm flange width, and 6mm thickness.' }
                    ]
                },
                {
                    name: 'Fasteners',
                    description: 'Create nuts, bolts, and screws.',
                    prompts: [
                        { name: 'Bolt and Nut', prompt: 'Create an M12 hex bolt with a length of 50mm and a corresponding M12 hex nut.' },
                        { name: 'Wood Screw', prompt: 'Create a wood screw with a length of 40mm and a diameter of 4mm.' },
                        { name: 'Socket Head Cap Screw', prompt: 'Create an M8 socket head cap screw with 30mm length.' },
                        { name: 'Wing Nut', prompt: 'Create an M10 wing nut for hand tightening applications.' },
                        { name: 'Threaded Rod', prompt: 'Create a 1-meter long M12 threaded rod with hex nuts on both ends.' }
                    ]
                },
                {
                    name: 'Mechanisms',
                    description: 'Create mechanical systems.',
                    prompts: [
                        { name: 'Slider-Crank', prompt: 'Create a slider-crank mechanism with a crank radius of 50mm and a connecting rod length of 150mm.' },
                        { name: 'Four-Bar Linkage', prompt: 'Create a four-bar linkage mechanism with link lengths 40mm, 80mm, 60mm, and 100mm.' },
                        { name: 'Cam and Follower', prompt: 'Create a cam and follower mechanism with a circular cam having a 5mm lift.' },
                        { name: 'Rack and Pinion', prompt: 'Create a rack and pinion system with a 20-tooth pinion gear and 200mm rack length.' }
                    ]
                },
                {
                    name: 'Motors & Actuators',
                    description: 'Create various types of motors and actuators.',
                    prompts: [
                        { name: 'Electric Motor', prompt: 'Create a 3-phase induction motor with NEMA 56 frame size and mounting feet.' },
                        { name: 'Servo Motor', prompt: 'Create a servo motor with encoder feedback and mounting flange.' },
                        { name: 'Stepper Motor', prompt: 'Create a NEMA 23 stepper motor with 4-wire configuration.' },
                        { name: 'Linear Actuator', prompt: 'Create an electric linear actuator with 100mm stroke and ball screw drive.' },
                        { name: 'Pneumatic Actuator', prompt: 'Create a rotary pneumatic actuator with 90-degree rotation angle.' }
                    ]
                },
                {
                    name: 'Pumps & Compressors',
                    description: 'Create fluid handling equipment.',
                    prompts: [
                        { name: 'Centrifugal Pump', prompt: 'Create a centrifugal pump with volute casing, impeller, and flanged connections.' },
                        { name: 'Gear Pump', prompt: 'Create an external gear pump with inlet and outlet ports.' },
                        { name: 'Piston Pump', prompt: 'Create a radial piston pump with 5 pistons and distribution valve.' },
                        { name: 'Diaphragm Pump', prompt: 'Create a double-diaphragm pump with air-operated drive.' },
                        { name: 'Scroll Compressor', prompt: 'Create a scroll compressor with fixed and orbiting scrolls.' }
                    ]
                },
                {
                    name: 'Heat Exchangers',
                    description: 'Create heat transfer equipment.',
                    prompts: [
                        { name: 'Shell and Tube Heat Exchanger', prompt: 'Create a shell and tube heat exchanger with 50 tubes and baffles.' },
                        { name: 'Plate Heat Exchanger', prompt: 'Create a plate heat exchanger with 10 plates and gaskets.' },
                        { name: 'Radiator', prompt: 'Create an automotive radiator with fins, tubes, and mounting brackets.' },
                        { name: 'Cooling Fins', prompt: 'Create aluminum cooling fins for electronic heat dissipation.' },
                        { name: 'Heat Sink', prompt: 'Create a CPU heat sink with vertical fins and mounting holes.' }
                    ]
                },
                {
                    name: 'Clutches & Brakes',
                    description: 'Create power transmission control components.',
                    prompts: [
                        { name: 'Disc Clutch', prompt: 'Create a single-plate dry clutch with pressure plate and friction disc.' },
                        { name: 'Drum Brake', prompt: 'Create a drum brake assembly with shoes, springs, and adjusters.' },
                        { name: 'Disc Brake', prompt: 'Create a disc brake with caliper, rotor, and brake pads.' },
                        { name: 'Electromagnetic Clutch', prompt: 'Create an electromagnetic clutch with coil, armature, and friction surface.' },
                        { name: 'Band Brake', prompt: 'Create a band brake system with flexible band and actuating mechanism.' }
                    ]
                },
                {
                    name: 'Chains & Sprockets',
                    description: 'Create chain drive systems.',
                    prompts: [
                        { name: 'Roller Chain', prompt: 'Create a section of #40 roller chain with 20 links.' },
                        { name: 'Sprocket', prompt: 'Create a 15-tooth sprocket for #40 roller chain with 1-inch bore.' },
                        { name: 'Chain Drive System', prompt: 'Create a complete chain drive with two sprockets and tensioner.' },
                        { name: 'Timing Chain', prompt: 'Create a timing chain system with guides and tensioner for an engine.' },
                        { name: 'Conveyor Chain', prompt: 'Create a heavy-duty conveyor chain with attachments.' }
                    ]
                },
                {
                    name: 'Cams & Profiles',
                    description: 'Create cam mechanisms and profiles.',
                    prompts: [
                        { name: 'Eccentric Cam', prompt: 'Create an eccentric cam with 5mm offset and 40mm diameter.' },
                        { name: 'Heart Cam', prompt: 'Create a heart-shaped cam profile for intermittent motion.' },
                        { name: 'Cam Follower', prompt: 'Create a roller cam follower with bearing and mounting bracket.' },
                        { name: 'Camshaft', prompt: 'Create a camshaft with 4 lobes for a 4-cylinder engine.' },
                        { name: 'Plate Cam', prompt: 'Create a plate cam with modified trapezoidal motion profile.' }
                    ]
                },
                {
                    name: 'Housings & Enclosures',
                    description: 'Create protective housings and enclosures.',
                    prompts: [
                        { name: 'Motor Housing', prompt: 'Create a motor housing with cooling fins and mounting feet.' },
                        { name: 'Gearbox Case', prompt: 'Create a split gearbox housing with bearing mounts and oil seals.' },
                        { name: 'Electronic Enclosure', prompt: 'Create a NEMA 4X electrical enclosure with hinged door.' },
                        { name: 'Pump Casing', prompt: 'Create a volute pump casing with suction and discharge nozzles.' },
                        { name: 'Bearing Housing', prompt: 'Create a pillow block bearing housing with grease fittings.' }
                    ]
                },
                {
                    name: 'Filters',
                    description: 'Create filtration components.',
                    prompts: [
                        { name: 'Air Filter', prompt: 'Create an automotive air filter with pleated media and rubber gasket.' },
                        { name: 'Oil Filter', prompt: 'Create a spin-on oil filter with bypass valve and anti-drainback valve.' },
                        { name: 'Fuel Filter', prompt: 'Create an inline fuel filter with 10-micron filtration.' },
                        { name: 'Hydraulic Filter', prompt: 'Create a return line hydraulic filter with indicator and bypass.' },
                        { name: 'Strainer', prompt: 'Create a Y-type strainer with removable screen and drain plug.' }
                    ]
                },
                {
                    name: 'Sensors & Instrumentation',
                    description: 'Create measurement and sensing devices.',
                    prompts: [
                        { name: 'Pressure Gauge', prompt: 'Create a bourdon tube pressure gauge with 4-inch dial and NPT connection.' },
                        { name: 'Thermometer', prompt: 'Create a bimetallic thermometer with 6-inch stem and dial indicator.' },
                        { name: 'Flow Meter', prompt: 'Create a turbine flow meter with digital display and pulse output.' },
                        { name: 'Level Sensor', prompt: 'Create a float-type level sensor with magnetic coupling.' },
                        { name: 'Proximity Sensor', prompt: 'Create an inductive proximity sensor with M18 threading.' }
                    ]
                },
                {
                    name: 'Joints & Connections',
                    description: 'Create mechanical joints and connections.',
                    prompts: [
                        { name: 'Welded Joint', prompt: 'Create a T-joint weld with fillet welds on both sides.' },
                        { name: 'Bolted Flange', prompt: 'Create a bolted flange connection with 8 bolts and gasket.' },
                        { name: 'Pin Joint', prompt: 'Create a clevis pin joint with bushings and safety clip.' },
                        { name: 'Threaded Connection', prompt: 'Create a threaded pipe connection with union fitting.' },
                        { name: 'Riveted Joint', prompt: 'Create a riveted lap joint with 6 rivets in two rows.' }
                    ]
                },
                {
                    name: 'Power Transmission',
                    description: 'Create power transmission components.',
                    prompts: [
                        { name: 'Drive Shaft', prompt: 'Create a drive shaft with CV joints for automotive application.' },
                        { name: 'Gear Reducer', prompt: 'Create a helical gear reducer with 10:1 ratio and mounting base.' },
                        { name: 'Belt Tensioner', prompt: 'Create an automatic belt tensioner with spring-loaded pulley.' },
                        { name: 'Flywheel', prompt: 'Create a flywheel with starter ring gear and mounting bolts.' },
                        { name: 'Differential', prompt: 'Create a simple automotive differential with side gears and pinions.' }
                    ]
                },
                {
                    name: 'Turbines & Impellers',
                    description: 'Create rotating machinery for fluid handling.',
                    prompts: [
                        { name: 'Centrifugal Impeller', prompt: 'Create a centrifugal impeller with 8 curved blades and eye diameter of 50mm.' },
                        { name: 'Axial Flow Impeller', prompt: 'Create an axial flow impeller with 6 twisted blades for a fan.' },
                        { name: 'Steam Turbine Blade', prompt: 'Create a steam turbine blade with airfoil profile and root attachment.' },
                        { name: 'Wind Turbine Rotor', prompt: 'Create a 3-blade wind turbine rotor with aerodynamic profiles.' },
                        { name: 'Pelton Wheel', prompt: 'Create a Pelton wheel turbine with 20 buckets for hydroelectric generation.' }
                    ]
                },
                {
                    name: 'Robots & Automation',
                    description: 'Create robotic and automation components.',
                    prompts: [
                        { name: 'Robot Arm Joint', prompt: 'Create a 6-DOF robot arm joint with servo motor and encoder.' },
                        { name: 'Gripper Assembly', prompt: 'Create a 2-finger pneumatic gripper with parallel jaw motion.' },
                        { name: 'Linear Guide', prompt: 'Create a linear guide rail with ball bearing carriage and end blocks.' },
                        { name: 'Rotary Table', prompt: 'Create a motorized rotary table with worm gear drive and indexing.' },
                        { name: 'Pick and Place Unit', prompt: 'Create a pneumatic pick and place unit with vacuum gripper.' }
                    ]
                },
                {
                    name: 'Material Handling',
                    description: 'Create equipment for moving and handling materials.',
                    prompts: [
                        { name: 'Conveyor Roller', prompt: 'Create a gravity conveyor roller with ball bearings and hex shaft.' },
                        { name: 'Bucket Elevator', prompt: 'Create a bucket elevator system with chain drive and discharge chute.' },
                        { name: 'Screw Conveyor', prompt: 'Create a screw conveyor with helical flight and trough housing.' },
                        { name: 'Overhead Crane', prompt: 'Create an overhead crane with trolley, hoist, and bridge structure.' },
                        { name: 'Forklift Mast', prompt: 'Create a forklift mast assembly with hydraulic cylinders and chains.' }
                    ]
                },
                {
                    name: 'Suspension Systems',
                    description: 'Create suspension and vibration control components.',
                    prompts: [
                        { name: 'Shock Absorber', prompt: 'Create a hydraulic shock absorber with piston, rod, and mounting brackets.' },
                        { name: 'Coil Spring', prompt: 'Create a variable rate coil spring for automotive suspension.' },
                        { name: 'Air Spring', prompt: 'Create an air spring with flexible bellows and mounting plates.' },
                        { name: 'Anti-Roll Bar', prompt: 'Create an automotive anti-roll bar with end links and bushings.' },
                        { name: 'Vibration Damper', prompt: 'Create a tuned mass damper for structural vibration control.' }
                    ]
                },
                {
                    name: 'Engine Components',
                    description: 'Create internal combustion engine parts.',
                    prompts: [
                        { name: 'Engine Block', prompt: 'Create a 4-cylinder engine block with cooling passages and mounting points.' },
                        { name: 'Crankshaft', prompt: 'Create a forged steel crankshaft with 4 throws and counterweights.' },
                        { name: 'Connecting Rod', prompt: 'Create a connecting rod with big end bearing and small end bushing.' },
                        { name: 'Valve Assembly', prompt: 'Create an intake valve with spring, retainer, and keeper assembly.' },
                        { name: 'Turbocharger', prompt: 'Create a turbocharger with turbine wheel, compressor wheel, and housing.' }
                    ]
                },
                {
                    name: 'Hydraulic Systems',
                    description: 'Create hydraulic power and control components.',
                    prompts: [
                        { name: 'Hydraulic Manifold', prompt: 'Create a hydraulic manifold block with multiple valve cavities and passages.' },
                        { name: 'Accumulator', prompt: 'Create a hydraulic accumulator with bladder, shell, and gas valve.' },
                        { name: 'Flow Control Valve', prompt: 'Create a flow control valve with adjustable orifice and bypass.' },
                        { name: 'Hydraulic Motor', prompt: 'Create a radial piston hydraulic motor with 5 pistons.' },
                        { name: 'Reservoir Tank', prompt: 'Create a hydraulic reservoir with filters, breather, and sight gauge.' }
                    ]
                },
                {
                    name: 'Pneumatic Systems',
                    description: 'Create pneumatic power and control components.',
                    prompts: [
                        { name: 'Air Compressor', prompt: 'Create a reciprocating air compressor with tank and pressure switch.' },
                        { name: 'Pneumatic Valve', prompt: 'Create a 5/2-way pneumatic directional control valve.' },
                        { name: 'Air Filter Regulator', prompt: 'Create an air filter-regulator-lubricator (FRL) unit.' },
                        { name: 'Quick Disconnect', prompt: 'Create a pneumatic quick disconnect coupling with safety lock.' },
                        { name: 'Vacuum Generator', prompt: 'Create a venturi vacuum generator with silencer and gauge port.' }
                    ]
                },
                {
                    name: 'Machine Tools',
                    description: 'Create machining and manufacturing equipment.',
                    prompts: [
                        { name: 'Lathe Chuck', prompt: 'Create a 3-jaw self-centering lathe chuck with hardened jaws.' },
                        { name: 'Milling Cutter', prompt: 'Create an end mill cutter with 4 flutes and carbide coating.' },
                        { name: 'Drill Bit', prompt: 'Create a twist drill bit with 118-degree point angle and flutes.' },
                        { name: 'Tool Holder', prompt: 'Create a CAT40 tool holder with retention knob and coolant passage.' },
                        { name: 'Vise', prompt: 'Create a machine vise with movable jaw, fixed jaw, and acme screw.' }
                    ]
                },
                {
                    name: 'Control Systems',
                    description: 'Create control and monitoring equipment.',
                    prompts: [
                        { name: 'Control Panel', prompt: 'Create an industrial control panel with HMI, PLC, and circuit breakers.' },
                        { name: 'Servo Drive', prompt: 'Create a servo motor drive with cooling fins and connector terminals.' },
                        { name: 'Variable Frequency Drive', prompt: 'Create a VFD with digital display and parameter keypad.' },
                        { name: 'Encoder', prompt: 'Create a rotary encoder with optical disk and photoelectric sensors.' },
                        { name: 'Control Valve', prompt: 'Create a pneumatic control valve with positioner and feedback.' }
                    ]
                },
                {
                    name: 'Safety Equipment',
                    description: 'Create safety and protection devices.',
                    prompts: [
                        { name: 'Safety Relief Valve', prompt: 'Create a spring-loaded safety relief valve with set pressure adjustment.' },
                        { name: 'Emergency Stop Button', prompt: 'Create a mushroom head emergency stop button with twist release.' },
                        { name: 'Light Curtain', prompt: 'Create a safety light curtain with transmitter and receiver units.' },
                        { name: 'Pressure Relief Device', prompt: 'Create a rupture disk assembly with holder and gaskets.' },
                        { name: 'Guard Rail', prompt: 'Create a machine guard rail system with posts and mesh panels.' }
                    ]
                },
                {
                    name: 'Welding & Fabrication',
                    description: 'Create welding and fabrication equipment.',
                    prompts: [
                        { name: 'Welding Torch', prompt: 'Create a MIG welding torch with trigger, gas nozzle, and contact tip.' },
                        { name: 'Welding Fixture', prompt: 'Create a welding fixture with clamps and positioning devices.' },
                        { name: 'Cutting Torch', prompt: 'Create an oxy-acetylene cutting torch with valves and tip.' },
                        { name: 'Welding Positioner', prompt: 'Create a welding positioner with rotating table and tilt mechanism.' },
                        { name: 'Fume Extractor', prompt: 'Create a welding fume extractor with flexible arm and filter.' }
                    ]
                },
                {
                    name: 'Precision Components',
                    description: 'Create high-precision mechanical components.',
                    prompts: [
                        { name: 'Ball Screw', prompt: 'Create a precision ball screw with nut and end supports.' },
                        { name: 'Linear Bearing', prompt: 'Create a linear ball bearing with retainer and seals.' },
                        { name: 'Optical Mount', prompt: 'Create a kinematic optical mount with adjustment screws.' },
                        { name: 'Micrometer', prompt: 'Create a mechanical micrometer with spindle, thimble, and ratchet.' },
                        { name: 'Gauge Block', prompt: 'Create a precision gauge block set with various thicknesses.' }
                    ]
                },
                {
                    name: 'Agricultural Equipment',
                    description: 'Create farming and agricultural machinery.',
                    prompts: [
                        { name: 'Plow Blade', prompt: 'Create a moldboard plow blade with curved surface and cutting edge.' },
                        { name: 'Harvester Header', prompt: 'Create a combine harvester header with cutting bar and reel.' },
                        { name: 'Irrigation Sprinkler', prompt: 'Create an impact irrigation sprinkler with rotating arm.' },
                        { name: 'Seed Drill', prompt: 'Create a seed drill unit with opener, seed tube, and closing wheel.' },
                        { name: 'Hay Baler', prompt: 'Create a round hay baler with pickup, chamber, and wrap mechanism.' }
                    ]
                },
                {
                    name: 'Mining Equipment',
                    description: 'Create mining and extraction equipment.',
                    prompts: [
                        { name: 'Conveyor Idler', prompt: 'Create a mining conveyor idler with heavy-duty bearings and seals.' },
                        { name: 'Crusher Jaw', prompt: 'Create a jaw crusher plate with wear-resistant surface.' },
                        { name: 'Ball Mill', prompt: 'Create a ball mill with shell, liners, and grinding media.' },
                        { name: 'Drill Bit', prompt: 'Create a mining drill bit with tungsten carbide inserts.' },
                        { name: 'Ventilation Fan', prompt: 'Create a mine ventilation fan with axial blades and motor.' }
                    ]
                },
                {
                    name: 'Construction Equipment',
                    description: 'Create heavy construction machinery components.',
                    prompts: [
                        { name: 'Excavator Bucket', prompt: 'Create an excavator bucket with teeth, cutting edge, and pivot pins.' },
                        { name: 'Bulldozer Blade', prompt: 'Create a bulldozer blade with cutting edge and hydraulic cylinders.' },
                        { name: 'Concrete Mixer', prompt: 'Create a concrete mixer drum with spiral flights and discharge chute.' },
                        { name: 'Pile Driver', prompt: 'Create a pile driving hammer with weight and guide system.' },
                        { name: 'Compactor Roller', prompt: 'Create a vibratory compactor roller with eccentric weights.' }
                    ]
                }
            ]
        },
        {
            name: 'Architecture & Buildings',
            description: 'Create structures, from single rooms to entire buildings.',
            subcategories: [
                {
                    name: 'Residential',
                    description: 'Create residential buildings and components.',
                    prompts: [
                        { name: 'Simple House', prompt: 'Create a simple two-story house with a gabled roof, a front door, and four windows.' },
                        { name: 'Staircase', prompt: 'Create a straight staircase with 15 steps. The total rise is 3 meters and the total run is 4 meters.' }
                    ]
                },
                {
                    name: 'Components',
                    description: 'Create architectural components.',
                    prompts: [
                        { name: 'Wall with Window', prompt: 'Create a wall that is 5 meters long and 2.5 meters high, with a window in the center that is 1 meter by 1 meter.' },
                        { name: 'Door', prompt: 'Create a wooden door that is 0.9 meters wide and 2.1 meters high.' },
                        { name: 'Gable Roof', prompt: 'Create a gable roof with a 30-degree slope for a house that is 10 meters wide.' }
                    ]
                },
                {
                    name: 'Landscape',
                    description: 'Create outdoor and landscape elements.',
                    prompts: [
                        { name: 'Picket Fence', prompt: 'Create a picket fence section that is 2 meters long and 1 meter high.' }
                    ]
                }
            ]
        },
        {
            name: 'Furniture & Rooms',
            description: 'Design interior spaces and the objects within them.',
            subcategories: [
                {
                    name: 'Tables',
                    description: 'Create various types of tables.',
                    prompts: [
                        { name: 'Dining Table', prompt: 'Create a rectangular dining table for 6 people with a wooden top and four legs.' },
                        { name: 'Coffee Table', prompt: 'Create a modern coffee table with a glass top and metal frame.' }
                    ]
                },
                {
                    name: 'Seating',
                    description: 'Create chairs, stools, and sofas.',
                    prompts: [
                        { name: 'Armchair', prompt: 'Create a comfortable armchair with fabric upholstery.' },
                        { name: 'Simple Stool', prompt: 'Create a simple three-legged wooden stool.' }
                    ]
                },
                {
                    name: 'Storage',
                    description: 'Create storage furniture.',
                    prompts: [
                        { name: 'Bookshelf', prompt: 'Create a 5-shelf bookshelf.' },
                        { name: 'Cabinet', prompt: 'Create a kitchen cabinet with two doors, 80cm wide and 60cm high.' }
                    ]
                },
                {
                    name: 'Beds',
                    description: 'Create different types of beds.',
                    prompts: [
                        { name: 'Single Bed', prompt: 'Create a simple single bed with a mattress and a bed frame.' }
                    ]
                }
            ]
        },
        {
            name: 'Product Configurators',
            description: 'Models with parametric variations.',
            subcategories: [
                {
                    name: 'Configurable Shelf',
                    description: 'A shelf system with configurable parameters.',
                    prompts: [
                        { name: 'Configurable Shelf', prompt: 'Create a configurable shelf system with adjustable width, depth, and number of shelves as input parameters.' },
                    ]
                },
                {
                    name: 'Customizable Ring',
                    description: 'A ring with configurable parameters.',
                    prompts: [
                        { name: 'Customizable Ring', prompt: 'Create a ring with a configurable inner and outer radius and depth.' }
                    ]
                }
            ]
        },
        {
            name: 'Science',
            description: 'Visualizations for scientific concepts.',
            subcategories: [
                {
                    name: 'Chemistry',
                    description: 'Models of molecules.',
                    prompts: [
                        { name: 'Methane Molecule', prompt: 'Create a model of a methane molecule (CH4) with one carbon atom and four hydrogen atoms.' }
                    ]
                },
                {
                    name: 'Physics',
                    description: 'Visualizations of physics principles.',
                    prompts: [
                        { name: 'Simple Pendulum', prompt: 'Create a simple pendulum with a string length of 1 meter and a bob with a radius of 5 cm.' }
                    ]
                },
                {
                    name: 'Biology',
                    description: 'Models for biology.',
                    prompts: [
                        { name: 'DNA Strand', prompt: 'Create a short segment of a DNA double helix.' }
                    ]
                }
            ]
        },
        {
            name: 'Everyday Objects',
            description: 'Common items.',
            subcategories: [
                {
                    name: 'Kitchenware',
                    description: 'Items found in a kitchen.',
                    prompts: [
                        { name: 'Coffee Mug', prompt: 'Create a ceramic coffee mug.' },
                        { name: 'Plate', prompt: 'Create a simple dinner plate.' },
                        { name: 'Wine Glass', prompt: 'Create a wine glass.' }
                    ]
                },
                {
                    name: 'Electronics',
                    description: 'Create electronic components and devices.',
                    prompts: [
                        { name: 'Smartphone', prompt: 'Create a simplified model of a smartphone.' },
                        { name: 'Resistor', prompt: 'Create a color-coded axial resistor.' }
                    ]
                },
                {
                    name: 'Tools',
                    description: 'Create common hand tools.',
                    prompts: [
                        { name: 'Hammer', prompt: 'Create a claw hammer.' },
                        { name: 'Screwdriver', prompt: 'Create a Phillips head screwdriver.' }
                    ]
                }
            ]
        },
        {
            name: 'Jewelry',
            description: 'Design rings, necklaces, and other adornments.',
            subcategories: [
                {
                    name: 'Rings',
                    description: 'Create different styles of rings.',
                    prompts: [
                        { name: 'Solitaire Ring', prompt: 'Create a solitaire diamond engagement ring.' }
                    ]
                }
            ]
        },
        {
            name: 'Automotive & Transportation',
            description: 'Vehicles and transportation-related components.',
            subcategories: [
                {
                    name: 'Vehicles',
                    description: 'Create various types of vehicles.',
                    prompts: [
                        { name: 'Simple Car', prompt: 'Create a basic car model with four wheels, body, and windows.' },
                        { name: 'Motorcycle', prompt: 'Create a motorcycle with two wheels, engine, and handlebars.' },
                        { name: 'Bicycle', prompt: 'Create a bicycle with frame, two wheels, pedals, and handlebars.' }
                    ]
                },
                {
                    name: 'Aircraft',
                    description: 'Create flying vehicles.',
                    prompts: [
                        { name: 'Airplane', prompt: 'Create a small airplane with wings, fuselage, and propeller.' },
                        { name: 'Helicopter', prompt: 'Create a helicopter with main rotor, tail rotor, and cabin.' }
                    ]
                },
                {
                    name: 'Marine',
                    description: 'Create water vehicles.',
                    prompts: [
                        { name: 'Sailboat', prompt: 'Create a sailboat with hull, mast, and sail.' },
                        { name: 'Speed Boat', prompt: 'Create a motorboat with streamlined hull and outboard motor.' }
                    ]
                }
            ]
        },
        {
            name: 'Sports & Recreation',
            description: 'Equipment and items for sports and recreational activities.',
            subcategories: [
                {
                    name: 'Ball Sports',
                    description: 'Equipment for ball sports.',
                    prompts: [
                        { name: 'Soccer Ball', prompt: 'Create a soccer ball with traditional black and white pattern.' },
                        { name: 'Basketball', prompt: 'Create a basketball with characteristic lines and orange color.' },
                        { name: 'Tennis Ball', prompt: 'Create a tennis ball with fuzzy texture and curved seam.' }
                    ]
                },
                {
                    name: 'Fitness Equipment',
                    description: 'Create gym and fitness equipment.',
                    prompts: [
                        { name: 'Dumbbell', prompt: 'Create a dumbbell with adjustable weight plates.' },
                        { name: 'Barbell', prompt: 'Create a standard Olympic barbell with weight plates.' },
                        { name: 'Exercise Bike', prompt: 'Create a stationary exercise bike with seat, pedals, and handlebars.' }
                    ]
                },
                {
                    name: 'Outdoor Recreation',
                    description: 'Equipment for outdoor activities.',
                    prompts: [
                        { name: 'Tent', prompt: 'Create a two-person camping tent with stakes and guy ropes.' },
                        { name: 'Backpack', prompt: 'Create a hiking backpack with multiple compartments and straps.' }
                    ]
                }
            ]
        },
        {
            name: 'Art & Decorative',
            description: 'Artistic and decorative objects.',
            subcategories: [
                {
                    name: 'Sculptures',
                    description: 'Create artistic sculptures.',
                    prompts: [
                        { name: 'Abstract Sculpture', prompt: 'Create an abstract geometric sculpture with curved and angular surfaces.' },
                        { name: 'Bust', prompt: 'Create a classical-style bust on a pedestal.' }
                    ]
                },
                {
                    name: 'Vases & Pottery',
                    description: 'Create decorative vessels.',
                    prompts: [
                        { name: 'Ceramic Vase', prompt: 'Create an elegant ceramic vase with a narrow neck and wide base.' },
                        { name: 'Clay Pot', prompt: 'Create a traditional clay pot with handles.' }
                    ]
                },
                {
                    name: 'Decorative Objects',
                    description: 'Create ornamental items.',
                    prompts: [
                        { name: 'Candlestick', prompt: 'Create a brass candlestick holder with drip tray.' },
                        { name: 'Picture Frame', prompt: 'Create an ornate picture frame for an 8x10 inch photo.' }
                    ]
                }
            ]
        },
        {
            name: 'Industrial Equipment',
            description: 'Machinery and industrial components.',
            subcategories: [
                {
                    name: 'Machinery',
                    description: 'Create industrial machines.',
                    prompts: [
                        { name: 'Conveyor Belt', prompt: 'Create a 5-meter long conveyor belt system with rollers and motor.' },
                        { name: 'Industrial Pump', prompt: 'Create a centrifugal pump with inlet and outlet flanges.' }
                    ]
                },
                {
                    name: 'Tanks & Vessels',
                    description: 'Create storage tanks and pressure vessels.',
                    prompts: [
                        { name: 'Storage Tank', prompt: 'Create a cylindrical storage tank with legs and access ladder.' },
                        { name: 'Pressure Vessel', prompt: 'Create a horizontal pressure vessel with domed ends and nozzles.' }
                    ]
                }
            ]
        },
        {
            name: 'Electrical Systems',
            description: 'Electrical components and systems.',
            subcategories: [
                {
                    name: 'Panels & Enclosures',
                    description: 'Create electrical enclosures.',
                    prompts: [
                        { name: 'Electrical Panel', prompt: 'Create an electrical distribution panel with circuit breakers.' },
                        { name: 'Junction Box', prompt: 'Create a weatherproof electrical junction box with conduit entries.' }
                    ]
                },
                {
                    name: 'Components',
                    description: 'Create electrical components.',
                    prompts: [
                        { name: 'Light Switch', prompt: 'Create a standard wall light switch with cover plate.' },
                        { name: 'Electrical Outlet', prompt: 'Create a duplex electrical outlet with ground fault protection.' }
                    ]
                }
            ]
        },
        {
            name: 'Plumbing & HVAC',
            description: 'Plumbing and heating, ventilation, and air conditioning components.',
            subcategories: [
                {
                    name: 'Pipes & Fittings',
                    description: 'Create pipe systems and fittings.',
                    prompts: [
                        { name: 'Pipe Elbow', prompt: 'Create a 90-degree pipe elbow fitting for 2-inch diameter pipe.' },
                        { name: 'T-Junction', prompt: 'Create a T-junction fitting for connecting three pipes.' }
                    ]
                },
                {
                    name: 'HVAC Components',
                    description: 'Create heating and cooling components.',
                    prompts: [
                        { name: 'Air Duct', prompt: 'Create a rectangular air duct with flanged connections.' },
                        { name: 'Radiator', prompt: 'Create a wall-mounted heating radiator with multiple fins.' }
                    ]
                }
            ]
        },
        {
            name: 'Gaming & Entertainment',
            description: 'Games, toys, and entertainment items.',
            subcategories: [
                {
                    name: 'Board Games',
                    description: 'Create board game components.',
                    prompts: [
                        { name: 'Chess Set', prompt: 'Create a complete chess set with board and all pieces.' },
                        { name: 'Dice', prompt: 'Create a set of six-sided dice with dots.' }
                    ]
                },
                {
                    name: 'Toys',
                    description: 'Create toy models.',
                    prompts: [
                        { name: 'Building Blocks', prompt: 'Create a set of interlocking building blocks like LEGO bricks.' },
                        { name: 'Toy Car', prompt: 'Create a simple toy car for children.' }
                    ]
                },
                {
                    name: 'Musical Instruments',
                    description: 'Create musical instruments.',
                    prompts: [
                        { name: 'Acoustic Guitar', prompt: 'Create an acoustic guitar with body, neck, and strings.' },
                        { name: 'Piano Keys', prompt: 'Create a section of piano keys with white and black keys.' }
                    ]
                }
            ]
        },
        {
            name: 'Garden & Outdoor',
            description: 'Gardening tools and outdoor equipment.',
            subcategories: [
                {
                    name: 'Garden Tools',
                    description: 'Create gardening implements.',
                    prompts: [
                        { name: 'Shovel', prompt: 'Create a garden shovel with wooden handle and metal blade.' },
                        { name: 'Watering Can', prompt: 'Create a metal watering can with spout and handle.' }
                    ]
                },
                {
                    name: 'Planters',
                    description: 'Create plant containers.',
                    prompts: [
                        { name: 'Flower Pot', prompt: 'Create a terracotta flower pot with drainage hole.' },
                        { name: 'Planter Box', prompt: 'Create a rectangular wooden planter box for vegetables.' }
                    ]
                },
                {
                    name: 'Outdoor Furniture',
                    description: 'Create furniture for outdoor use.',
                    prompts: [
                        { name: 'Park Bench', prompt: 'Create a wooden park bench with metal frame.' },
                        { name: 'Patio Chair', prompt: 'Create a comfortable patio chair with cushions.' }
                    ]
                }
            ]
        }
    ];
} 